import {
  RESEND_COOLDOWN_SECONDS,
  type ResendCodeRequest,
  type VerificationChannelName,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import { Button } from '@instanexus/ui';
import { useEffect, useState, type ReactElement } from 'react';

import { CaptchaField } from '@/components/captcha-field';
import { useApiClient } from '@/hooks/use-api-client';
import { useCaptcha } from '@/hooks/use-captcha';

/** Same fallback reasoning as `login-page.tsx` — never a declared `ErrorCode`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';

export interface ResendCodeButtonProps {
  readonly channel: VerificationChannelName;
  readonly email: string;
  /** Required when `channel` is `'SMS'`, ignored otherwise. */
  readonly phone?: string;
}

type ResendStatus = 'idle' | 'sending' | 'sent' | 'error';

/**
 * "Send a new code" — the one control that makes a stranded verification recoverable without
 * an operator (S26-TASK-021). Used by BOTH verification screens; defined once, because a second
 * copy is a second place for the copy below to drift.
 *
 * THE CONFIRMATION IS CONDITIONAL, AND THAT IS THE UI HALF OF A SECURITY DECISION.
 * `POST /api/web/auth/resend-code` answers `204` with an empty body for a pending account, an
 * active one, a verified channel, a suspended tenant, an address with no account at all, a
 * caller inside the 60-second floor, a target past its hourly ceiling and a disallowed SMS
 * country — eight branches, one response (S26 Constraint 5). So this control may never say "we
 * sent you a code": it says "IF this account is still waiting to be verified…", which is the
 * only wording true of all eight, and is the same device `forgot-password`'s neutral
 * confirmation uses.
 *
 * Its four states, the same four the central forgot-password page establishes:
 *  - idle       — an enabled link-styled button.
 *  - submitting — `disabled` + `aria-busy`, so a double-click cannot send two codes.
 *  - cooldown   — `disabled` for `RESEND_COOLDOWN_SECONDS` after ANY settled response, success
 *                 or failure, so a `429` does not leave a button the user can keep hammering.
 *  - error      — `VALIDATION_FAILED` or `RATE_LIMITED`, the only two failures this route can
 *                 produce, rendered through the shared `errors.json` copy.
 *
 * THE COOLDOWN IS COMPONENT STATE AND DOES NOT SURVIVE A RELOAD, deliberately. It is a
 * courtesy, not a control: the server enforces the same floor independently against
 * `VerificationCode.createdAt`, so a reload buys a caller a button that answers `204` and sends
 * nothing. Persisting it, returning the remaining seconds in a body, or adding a `Retry-After`
 * would each re-open the oracle the empty `204` exists to close.
 */
export function ResendCodeButton({ channel, email, phone }: ResendCodeButtonProps): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();

  const captcha = useCaptcha();
  const [status, setStatus] = useState<ResendStatus>('idle');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  /** One tick per second while the floor is running. Cleared on unmount by the returned
   *  teardown, so a screen navigated away from leaves no timer behind. */
  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timer = setTimeout(() => {
      setCooldownSeconds((seconds) => seconds - 1);
    }, 1_000);
    return () => {
      clearTimeout(timer);
    };
  }, [cooldownSeconds]);

  /** Back to idle once the floor has elapsed: the status node goes away with it, and a stale
   *  error does not sit under a button that now works. */
  useEffect(() => {
    if (cooldownSeconds > 0) return;
    setStatus((current) => (current === 'sent' || current === 'error' ? 'idle' : current));
    setErrorCode((current) => (current === null ? current : null));
  }, [cooldownSeconds]);

  const isSending = status === 'sending';
  const isDisabled = isSending || cooldownSeconds > 0 || captcha.token === null;

  const onResend = (): void => {
    if (captcha.token === null) return;
    const captchaToken = captcha.token;
    setStatus('sending');
    setErrorCode(null);

    /* The discriminated union is built HERE rather than spread from props, so the EMAIL arm
       cannot carry a stray `phone` key: `resendCodeRequestSchema` is strict about which arm it
       is looking at, and an extra field is a field the route would have to decide about. */
    const request: ResendCodeRequest =
      channel === 'SMS'
        ? { channel: 'SMS', email, phone: phone ?? '', captchaToken }
        : { channel: 'EMAIL', email, captchaToken };

    void api.auth
      .resendCode(request)
      .then(() => {
        setStatus('sent');
      })
      .catch((error: unknown) => {
        setErrorCode(isApiError(error) ? error.code : UNEXPECTED_FAILURE);
        setStatus('error');
      })
      .finally(() => {
        // ANY settled response starts the floor — a refusal is exactly when a user is most
        // tempted to press again, and the server would refuse that one too, silently.
        setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
        // Single-use token: re-arm after every attempt, whatever happened.
        captcha.reset();
      });
  };

  return (
    <div className="flex flex-col items-stretch gap-2">
      <CaptchaField captcha={captcha} />
      <Button
        // Load-bearing: this control sits inside the verification `<form>`, and a default
        // `submit` would fire the verify handler against an empty code field.
        type="button"
        variant="link"
        className="w-full"
        onClick={onResend}
        disabled={isDisabled}
        aria-busy={isSending}
      >
        {isSending ? t('resend.sending') : t('resend.button')}
      </Button>

      {errorCode === null ? null : (
        <p role="alert" className="text-danger text-sm text-pretty">
          {errorMessage(t, errorCode)}
        </p>
      )}

      {cooldownSeconds > 0 ? (
        // `status`, not `alert`: nothing has gone wrong, so it is announced politely.
        <div role="status" aria-label={t('resend.srStatus')} className="flex flex-col gap-1">
          {status === 'sent' ? (
            <p className="text-muted text-sm text-pretty">{t('resend.sent')}</p>
          ) : null}
          <p className="text-muted text-sm text-pretty">
            {t('resend.cooldown', { seconds: cooldownSeconds })}
          </p>
        </div>
      ) : null}
    </div>
  );
}
